import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import { useALCContext, ALC_STATES } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import { saveALCAttachmentToIndexedDB } from '../services/ALCOfflineService';
import ALCStepper from './ALCStepper';
import store from '../../../../store/store';
import { addPendingSync } from '../../../../store/ALCSlice';
import { ALC_CHECKLIST_SEED_DATA, getALCSections, TOTAL_ALC_QUESTIONS, MAX_ALC_SCORE } from '../constants/alcSeedData';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { getAccessToken } from '../../../../Services/getAccessToken';

interface CheckpointResponse {
    score: string;
    remarks: string;
}

const ALCChecklist: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { state, dispatch, isProductionUser, isProductUser } = useALCContext();
    const navigate = useNavigate();

    const sections = useMemo(() => getALCSections(), []);

    const [responses, setResponses] = useState<Record<number, CheckpointResponse>>({});
    const [uploadedFiles, setUploadedFiles] = useState<Record<number, File[]>>({});
    const [existingRecords, setExistingRecords] = useState<Record<number, any>>({});
    const [existingFiles, setExistingFiles] = useState<Record<number, string[]>>({});
    const [invalidFields, setInvalidFields] = useState<Record<string, boolean>>({});

    // Pre-populate saved checkpoints if resuming
    useEffect(() => {
        const loadExisting = async () => {
            if (!state.currentTourId) return;

            try {
                const checkpoints = await alcService.getCheckpoints(state.currentTourId);
                const initResponses: Record<number, CheckpointResponse> = {};
                const initRecords: Record<number, any> = {};
                const initFiles: Record<number, string[]> = {};

                checkpoints.forEach((cp: any) => {
                    const cleanCriteria = (cp.cr3ea_criteria || "").replace(/CRITICAL(?: GATE)?/gi, "").trim().toLowerCase();
                    const matchedItem = ALC_CHECKLIST_SEED_DATA.find(item => {
                        const itemTitle = item.title.trim().toLowerCase();
                        return itemTitle === cleanCriteria || cleanCriteria.includes(itemTitle) || itemTitle.includes(cleanCriteria);
                    });

                    if (matchedItem) {
                        const id = matchedItem.id;
                        initRecords[id] = cp;

                        let selectVal = "Compliant (2)";
                        if (cp.cr3ea_defectcategory) {
                            selectVal = cp.cr3ea_defectcategory;
                        } else if (cp.cr3ea_status === "Not Okay") {
                            selectVal = "Non-Compliant (0)";
                        }

                        let cleanRemarks = cp.cr3ea_defectremarks || "";
                        let savedFiles: string[] = [];
                        if (cleanRemarks.includes("File:")) {
                            const fileIdx = cleanRemarks.indexOf("File:");
                            const filePart = cleanRemarks.substring(fileIdx + 5).trim();
                            let textPart = cleanRemarks.substring(0, fileIdx).trim();
                            if (textPart.endsWith("|")) {
                                textPart = textPart.substring(0, textPart.length - 1).trim();
                            }
                            cleanRemarks = textPart;
                            savedFiles = filePart.split(",").map((f: string) => f.trim()).filter(Boolean);
                        }

                        initResponses[id] = { score: selectVal, remarks: cleanRemarks };
                        if (savedFiles.length > 0) {
                            initFiles[id] = savedFiles;
                        }
                    }
                });

                setResponses(initResponses);
                setExistingRecords(initRecords);
                setExistingFiles(initFiles);
            } catch (e) {
                console.warn("Failed to load existing checkpoints", e);
            }
        };

        if (navigator.onLine) {
            loadExisting();
        }
    }, [state.currentTourId]);

    const handleSelectChange = (id: number, value: string) => {
        setResponses(prev => ({
            ...prev,
            [id]: { score: value, remarks: prev[id]?.remarks || "" }
        }));
        setInvalidFields(prev => ({
            ...prev,
            [`select_${id}`]: false
        }));
    };

    const handleRemarksChange = (id: number, value: string) => {
        setResponses(prev => ({
            ...prev,
            [id]: { score: prev[id]?.score || "", remarks: value }
        }));
        if (value.trim()) {
            setInvalidFields(prev => ({
                ...prev,
                [`remarks_${id}`]: false
            }));
        }
    };

    const handleFileChange = (id: number, files: FileList | null) => {
        if (!files || files.length === 0) return;

        const selectedFiles = Array.from(files);
        const validImages = selectedFiles.filter(file =>
            file.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(file.name)
        );

        if (validImages.length < selectedFiles.length) {
            alert(`${selectedFiles.length - validImages.length} non-image file(s) were ignored. Only images (JPG, PNG, WebP) are allowed.`);
        }

        setUploadedFiles(prev => {
            const current = prev[id] || [];
            // Prevent duplicates
            const filtered = validImages.filter(f => !current.some(cf => cf.name === f.name && cf.size === f.size));
            return {
                ...prev,
                [id]: [...current, ...filtered]
            };
        });

        if (validImages.length > 0) {
            setInvalidFields(prev => ({
                ...prev,
                [`file_${id}`]: false
            }));
        }
    };

    const handleRemoveFile = (id: number, fileIdx: number) => {
        setUploadedFiles(prev => {
            const current = prev[id] || [];
            const updated = current.filter((_, idx) => idx !== fileIdx);
            if (updated.length === 0) {
                const copy = { ...prev };
                delete copy[id];
                return copy;
            }
            return { ...prev, [id]: updated };
        });
    };

    // Live Progress Tracker banner calculations
    const trackerStats = useMemo(() => {
        let filledCount = 0;
        let compliantCount = 0;
        let partialCount = 0;
        let nonCompliantCount = 0;
        let pendingCount = 0;
        let hasLiveCriticalFailure = false;
        let liveCriticalFailureCount = 0;
        let totalObtainedScore = 0;

        ALC_CHECKLIST_SEED_DATA.forEach(q => {
            const res = responses[q.id];
            if (res && res.score) {
                filledCount++;
                let numScore = 2;
                if (res.score.includes("Non-Compliant") || res.score.includes("(0)") || res.score === "00") {
                    numScore = 0;
                    nonCompliantCount++;
                } else if (res.score.includes("Partial") || res.score.includes("(1)") || res.score === "01") {
                    numScore = 1;
                    partialCount++;
                } else {
                    compliantCount++;
                }

                totalObtainedScore += numScore;

                if (q.isCritical && (numScore === 0 || numScore === 1)) {
                    hasLiveCriticalFailure = true;
                    liveCriticalFailureCount++;
                }
            } else {
                pendingCount++;
            }
        });

        const progressPercent = TOTAL_ALC_QUESTIONS > 0 ? Math.round((filledCount / TOTAL_ALC_QUESTIONS) * 100) : 0;
        const estimatedScorePercent = MAX_ALC_SCORE > 0 ? ((totalObtainedScore / MAX_ALC_SCORE) * 100).toFixed(2) : "0.00";
        const isPass = parseFloat(estimatedScorePercent) >= 80 && !hasLiveCriticalFailure;

        return {
            filledCount,
            compliantCount,
            partialCount,
            nonCompliantCount,
            pendingCount,
            progressPercent,
            estimatedScorePercent,
            hasLiveCriticalFailure,
            liveCriticalFailureCount,
            isPass
        };
    }, [responses]);

    // Validation & Scoring function
    const evaluateChecklist = () => {
        const errors: Record<string, boolean> = {};
        let totalMaxScore = 0;
        let totalObtainedScore = 0;
        let hasDefects = false;
        let hasCriticalFailure = false;
        const criticalFailures: any[] = [];
        let incompleteCount = 0;
        let missingRemarksCount = 0;
        let missingFilesCount = 0;
        const rowRecords: any[] = [];

        ALC_CHECKLIST_SEED_DATA.forEach(q => {
            const res = responses[q.id];
            const scoreValue = res?.score || "";
            const remarksVal = res?.remarks?.trim() || "";
            const itemFiles = uploadedFiles[q.id] || [];
            const savedFilesList = existingFiles[q.id] || [];
            const hasAnyFiles = itemFiles.length > 0 || savedFilesList.length > 0;

            if (!scoreValue) {
                incompleteCount++;
                errors[`select_${q.id}`] = true;
            }

            let numScore = 2;
            let isNonCompliant = false;

            if (scoreValue.includes("(0)") || scoreValue.includes("Non-Compliant") || scoreValue === "00") {
                numScore = 0;
                hasDefects = true;
                isNonCompliant = true;
            } else if (scoreValue.includes("(1)") || scoreValue.includes("Partial") || scoreValue === "01") {
                numScore = 1;
                hasDefects = true;
            } else if (scoreValue.includes("(2)") || scoreValue.includes("Compliant") || scoreValue === "02") {
                numScore = 2;
            }

            // Mandatory remarks for Non-Compliant (0) or Partial (1)
            if ((numScore === 0 || numScore === 1) && !remarksVal) {
                missingRemarksCount++;
                errors[`remarks_${q.id}`] = true;
            }

            // Mandatory photo for Non-Compliant (0)
            if (isNonCompliant && !hasAnyFiles) {
                missingFilesCount++;
                errors[`file_${q.id}`] = true;
            }

            // Critical Gate check
            if (q.isCritical && (numScore === 0 || numScore === 1)) {
                hasCriticalFailure = true;
                criticalFailures.push({
                    id: q.id,
                    criteria: q.title,
                    score: numScore,
                    area: q.area,
                    remarks: remarksVal || scoreValue
                });
            }

            totalObtainedScore += numScore;
            totalMaxScore += 2;

            rowRecords.push({
                item: q,
                scoreValue: scoreValue || "Compliant (2)",
                numScore,
                remarks: remarksVal,
                files: itemFiles,
                existingSavedFiles: savedFilesList
            });
        });

        setInvalidFields(errors);

        return {
            incompleteCount,
            missingRemarksCount,
            missingFilesCount,
            totalObtainedScore,
            totalMaxScore,
            hasDefects,
            hasCriticalFailure,
            criticalFailures,
            rowRecords
        };
    };

    // Pause tour handler
    const handlePauseTour = async () => {
        if (!state.currentTourId) {
            alert("No active session ID found.");
            return;
        }

        const confirmPause = window.confirm(
            "Are you sure you want to pause the tour? Your progress will be saved, and you can resume it later from the dashboard."
        );
        if (!confirmPause) return;

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            // Calculate current obtained points out of total 92 max points
            let filledCount = 0;
            let totalObtained = 0;
            const recordsToSave: any[] = [];
            const filesToUploadList: { file: File, area: string, checkpointId: string, remarks: string, id: number }[] = [];

            ALC_CHECKLIST_SEED_DATA.forEach(q => {
                const res = responses[q.id];
                if (res && res.score) {
                    filledCount++;
                    let nScore = 2;
                    if (res.score.includes("Non-Compliant") || res.score.includes("(0)") || res.score === "00") {
                        nScore = 0;
                    } else if (res.score.includes("Partial") || res.score.includes("(1)") || res.score === "01") {
                        nScore = 1;
                    }
                    totalObtained += nScore;

                    const files = uploadedFiles[q.id] || [];
                    let finalRemarks = res.remarks || "";

                    const status = nScore < 2 ? "Not Okay" : "OK";
                    const existingCp = existingRecords[q.id];

                    const rowPayload: any = {
                        cr3ea_qualitytourid: state.currentTourId,
                        cr3ea_title: `ALC_${moment().format('MM-DD-YYYY')}`,
                        cr3ea_cycle: `Cycle-1`,
                        cr3ea_area: q.area,
                        cr3ea_criteria: q.title,
                        cr3ea_status: status,
                        cr3ea_defectcategory: res.score,
                        cr3ea_defectremarks: finalRemarks,
                        id: q.id
                    };

                    if (existingCp?.cr3ea_rajpura_alcsid) {
                        rowPayload.cr3ea_rajpura_alcsid = existingCp.cr3ea_rajpura_alcsid;
                    } else if (!navigator.onLine) {
                        rowPayload.cr3ea_rajpura_alcsid = uuidv4();
                        rowPayload._isNew = true;
                    }

                    recordsToSave.push(rowPayload);

                    files.forEach(f => {
                        filesToUploadList.push({
                            file: f,
                            area: q.area,
                            checkpointId: existingCp?.cr3ea_rajpura_alcsid || `CP-${q.id}`,
                            remarks: finalRemarks,
                            id: q.id
                        });
                    });
                }
            });

            const currentScore = ((totalObtained / MAX_ALC_SCORE) * 100).toFixed(2);

            if (!navigator.onLine) {
                // Offline pause save
                const syncPayloads: any[] = [];
                for (const row of recordsToSave) {
                    const rowId = row.id;
                    const itemFiles = uploadedFiles[rowId] || [];
                    for (const f of itemFiles) {
                        await saveALCAttachmentToIndexedDB(
                            state.currentTourId,
                            row.cr3ea_rajpura_alcsid || `temp_${rowId}`,
                            row.cr3ea_area,
                            row.cr3ea_defectremarks,
                            f
                        );
                    }
                    syncPayloads.push({
                        id: `${Date.now()}_checkrow_${rowId}`,
                        type: 'CHECKLIST_ROW',
                        method: row._isNew ? 'POST' : 'PATCH',
                        payload: row,
                        createdAt: Date.now()
                    });
                }

                syncPayloads.push({
                    id: `${Date.now()}_session_${state.currentTourId}`,
                    type: 'SESSION_UPDATE',
                    method: 'PATCH',
                    payload: {
                        cr3ea_prod_qualitytourid: state.currentTourId,
                        cr3ea_status: "QA In Progress",
                        cr3ea_processstatus: "QA In Progress",
                        cr3ea_overall_score: String(currentScore)
                    },
                    createdAt: Date.now()
                });

                store.dispatch(addPendingSync(syncPayloads));
                alert("Tour paused and progress saved offline! You can resume from dashboard when online.");
                navigate('/home');
                return;
            }

            // Online upload
            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;

            // 1. Upload defect photos
            const uploadedNamesMap: Record<number, string[]> = {};
            for (const task of filesToUploadList) {
                try {
                    const uploadedUrl = await alcService.uploadCorrectiveActionFile(
                        task.file,
                        state.currentTourId,
                        task.area,
                        task.checkpointId,
                        task.remarks,
                        accessToken,
                        instance,
                        accounts[0]
                    );
                    const fileName = uploadedUrl ? uploadedUrl.substring(uploadedUrl.lastIndexOf("/") + 1) : task.file.name;
                    if (!uploadedNamesMap[task.id]) uploadedNamesMap[task.id] = [];
                    uploadedNamesMap[task.id].push(fileName);
                } catch (upErr) {
                    console.warn(`Failed to upload photo for paused checkpoint ${task.checkpointId}:`, upErr);
                }
            }

            // 2. Save checklist rows in chunks
            const savePromises = recordsToSave.map(async (row) => {
                const newFiles = uploadedNamesMap[row.id] || [];
                const prevSaved = existingFiles[row.id] || [];
                const allFiles = Array.from(new Set([...prevSaved, ...newFiles]));
                if (allFiles.length > 0) {
                    row.cr3ea_defectremarks = row.cr3ea_defectremarks
                        ? `${row.cr3ea_defectremarks} | File: ${allFiles.join(', ')}`
                        : ` | File: ${allFiles.join(', ')}`;
                }
                return alcService.saveChecklistRow(row);
            });

            await Promise.all(savePromises);

            // 3. Update session status
            await alcService.saveSession({
                cr3ea_prod_qualitytourid: state.currentTourId,
                cr3ea_status: "QA In Progress",
                cr3ea_processstatus: "QA In Progress",
                cr3ea_overall_score: String(currentScore)
            });

            alert("Tour paused and progress saved successfully!");
            navigate('/home');

        } catch (error: any) {
            console.error("Error pausing tour:", error);
            alert("Failed to pause tour: " + error.message);
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Submit checklist handler
    const handleSubmit = async () => {
        if (!state.currentTourId) {
            alert("No active session ID found.");
            return;
        }

        const evalResult = evaluateChecklist();

        if (evalResult.incompleteCount > 0 || evalResult.missingRemarksCount > 0 || evalResult.missingFilesCount > 0) {
            let msg = "";
            if (evalResult.incompleteCount > 0) {
                msg += `Please select compliance score for the remaining ${evalResult.incompleteCount} checkpoint(s).\n`;
            }
            if (evalResult.missingRemarksCount > 0) {
                msg += `Please provide defect remarks for the ${evalResult.missingRemarksCount} non-compliant/partial checkpoint(s).\n`;
            }
            if (evalResult.missingFilesCount > 0) {
                msg += `Uploading a proof image is mandatory for ${evalResult.missingFilesCount} Non-Compliant checkpoint(s).`;
            }
            alert(msg.trim());

            // Scroll to the first invalid field
            const firstErrorKey = Object.keys(invalidFields).find(k => invalidFields[k]);
            if (firstErrorKey) {
                const el = document.getElementById(firstErrorKey);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const scorePercentRaw = (evalResult.totalObtainedScore / MAX_ALC_SCORE) * 100;
        const scorePercent = scorePercentRaw.toFixed(2);
        let isPass = (parseFloat(scorePercent) >= 80) && !evalResult.hasCriticalFailure;

        let statusText = "Completed";
        const isPreviousDay = state.isPreviousDay;

        if (isPreviousDay) {
            isPass = false;
            statusText = "Closed - Expired";
        } else if (evalResult.hasCriticalFailure) {
            isPass = false;
            statusText = "Failed - Pending Production";
        } else if (evalResult.hasDefects) {
            statusText = isPass ? "Success - Pending Production" : "Failed - Pending Production";
        }

        const saveOffline = async () => {
            const syncPayloads: any[] = [];

            for (const r of evalResult.rowRecords) {
                const qId = r.item.id;
                const files = r.files;
                const existingSaved = r.existingSavedFiles;
                let finalRemarks = r.remarks;

                if (files.length > 0) {
                    finalRemarks = finalRemarks
                        ? `${finalRemarks} | File: ${files.map((f: File) => f.name).join(', ')}`
                        : ` | File: ${files.map((f: File) => f.name).join(', ')}`;

                    for (const f of files) {
                        await saveALCAttachmentToIndexedDB(
                            state.currentTourId!,
                            existingRecords[qId]?.cr3ea_rajpura_alcsid || `temp_${qId}`,
                            r.item.area,
                            finalRemarks,
                            f
                        );
                    }
                } else if (existingSaved.length > 0) {
                    finalRemarks = finalRemarks
                        ? `${finalRemarks} | File: ${existingSaved.join(', ')}`
                        : ` | File: ${existingSaved.join(', ')}`;
                }

                if (finalRemarks.length > 1000) {
                    finalRemarks = finalRemarks.substring(0, 997) + "...";
                }

                const status = r.numScore < 2 ? "Not Okay" : "OK";
                const rowPayload: any = {
                    cr3ea_qualitytourid: state.currentTourId,
                    cr3ea_title: `ALC_${moment().format('MM-DD-YYYY')}`,
                    cr3ea_cycle: `Cycle-1`,
                    cr3ea_area: r.item.area,
                    cr3ea_criteria: r.item.title,
                    cr3ea_status: status,
                    cr3ea_defectcategory: r.scoreValue,
                    cr3ea_defectremarks: finalRemarks,
                    id: qId
                };

                if (existingRecords[qId]?.cr3ea_rajpura_alcsid) {
                    rowPayload.cr3ea_rajpura_alcsid = existingRecords[qId].cr3ea_rajpura_alcsid;
                } else {
                    rowPayload.cr3ea_rajpura_alcsid = uuidv4();
                    rowPayload._isNew = true;
                }

                syncPayloads.push({
                    id: `${Date.now()}_checkrow_${qId}`,
                    type: 'CHECKLIST_ROW',
                    method: rowPayload._isNew ? 'POST' : 'PATCH',
                    payload: rowPayload,
                    createdAt: Date.now()
                });
            }

            const dbStatusValue = statusText === "Success - Pending Production" ? "Failed - Pending Production" : statusText;
            const isLineClearVal = isPass && !isPreviousDay;

            syncPayloads.push({
                id: `${Date.now()}_session_${state.currentTourId}`,
                type: 'SESSION_UPDATE',
                method: 'PATCH',
                payload: {
                    cr3ea_prod_qualitytourid: state.currentTourId,
                    cr3ea_status: dbStatusValue,
                    cr3ea_processstatus: statusText,
                    cr3ea_overall_score: String(scorePercent),
                    cr3ea_checklist_result: isPreviousDay ? "Expired" : (isPass ? "Pass" : "Fail"),
                    cr3ea_islineclear: isLineClearVal
                },
                createdAt: Date.now()
            });

            store.dispatch(addPendingSync(syncPayloads));

            if (isPreviousDay) {
                alert(`Observations Submitted Offline! Since this is a previous day's observation, the session has been closed as Expired.`);
            } else if (evalResult.hasCriticalFailure) {
                alert(`ALC Checklist Failed (Critical Gate Violation). Score: ${scorePercent}%. ${evalResult.criticalFailures.length} critical parameter(s) failed. Saved offline. Please sync when online.`);
            } else if (statusText === "Success - Pending Production") {
                alert(`ALC Checklist Submitted successfully with Success Score: ${scorePercent}%. Saved offline. Forwarding to production.`);
            } else if (statusText === "Completed") {
                alert(`ALC Cleared Successfully! Score: ${scorePercent}%. Saved offline.`);
            } else {
                alert(`ALC Checklist Failed. Score: ${scorePercent}%. Saved offline.`);
            }

            navigate('/home');
        };

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            if (!navigator.onLine) {
                await saveOffline();
                return;
            }

            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;

            // 1. Upload proof files in parallel
            const uploadedNamesMap: Record<number, string[]> = {};
            const uploadTasks: any[] = [];

            evalResult.rowRecords.forEach(r => {
                const files = r.files || [];
                files.forEach((f: File) => {
                    uploadTasks.push({
                        file: f,
                        id: r.item.id,
                        area: r.item.area,
                        remarks: r.remarks,
                        checkpointId: existingRecords[r.item.id]?.cr3ea_rajpura_alcsid || `CP-${r.item.id}`
                    });
                });
            });

            for (const task of uploadTasks) {
                try {
                    const uploadedUrl = await alcService.uploadCorrectiveActionFile(
                        task.file,
                        state.currentTourId,
                        task.area,
                        task.checkpointId,
                        task.remarks,
                        accessToken,
                        instance,
                        accounts[0]
                    );
                    const fileName = uploadedUrl ? uploadedUrl.substring(uploadedUrl.lastIndexOf("/") + 1) : task.file.name;
                    if (!uploadedNamesMap[task.id]) uploadedNamesMap[task.id] = [];
                    uploadedNamesMap[task.id].push(fileName);
                } catch (upErr) {
                    console.warn(`Failed to upload photo for checkpoint ${task.checkpointId}:`, upErr);
                }
            }

            // 2. Prepare & save checkpoints
            const recordsToSave: any[] = [];
            const failedItemsForState: any[] = [];

            evalResult.rowRecords.forEach(r => {
                const qId = r.item.id;
                const newFiles = uploadedNamesMap[qId] || [];
                const prevFiles = r.existingSavedFiles || [];
                const allFiles = Array.from(new Set([...prevFiles, ...newFiles]));

                let finalRemarks = r.remarks;
                if (allFiles.length > 0) {
                    finalRemarks = finalRemarks
                        ? `${finalRemarks} | File: ${allFiles.join(', ')}`
                        : ` | File: ${allFiles.join(', ')}`;
                }

                if (finalRemarks.length > 1000) {
                    finalRemarks = finalRemarks.substring(0, 997) + "...";
                }

                const status = r.numScore < 2 ? "Not Okay" : "OK";
                const rowPayload: any = {
                    cr3ea_qualitytourid: state.currentTourId,
                    cr3ea_title: `ALC_${moment().format('MM-DD-YYYY')}`,
                    cr3ea_cycle: `Cycle-1`,
                    cr3ea_area: r.item.area,
                    cr3ea_criteria: r.item.title,
                    cr3ea_status: status,
                    cr3ea_defectcategory: r.scoreValue,
                    cr3ea_defectremarks: finalRemarks
                };

                if (existingRecords[qId]?.cr3ea_rajpura_alcsid) {
                    rowPayload.cr3ea_rajpura_alcsid = existingRecords[qId].cr3ea_rajpura_alcsid;
                }

                recordsToSave.push(rowPayload);

                if (r.numScore < 2) {
                    failedItemsForState.push(rowPayload);
                }
            });

            // Save checkpoints in parallel chunks of 8
            const CHUNK_SIZE = 8;
            for (let i = 0; i < recordsToSave.length; i += CHUNK_SIZE) {
                const chunk = recordsToSave.slice(i, i + CHUNK_SIZE);
                await Promise.all(chunk.map(rec => alcService.saveChecklistRow(rec)));
            }

            // 3. Update session in Dataverse
            const dbStatusValue = statusText === "Success - Pending Production" ? "Failed - Pending Production" : statusText;
            const isLineClearVal = isPass && !isPreviousDay;

            await alcService.saveSession({
                cr3ea_prod_qualitytourid: state.currentTourId,
                cr3ea_status: dbStatusValue,
                cr3ea_processstatus: statusText,
                cr3ea_overall_score: String(scorePercent),
                cr3ea_checklist_result: isPreviousDay ? "Expired" : (isPass ? "Pass" : "Fail"),
                cr3ea_islineclear: isLineClearVal
            });

            dispatch({ type: 'SET_FAILED_CHECKPOINTS', payload: failedItemsForState });

            // User notification
            if (isPreviousDay) {
                alert(`Observations Submitted Successfully! Since this is a previous day's observation, the session has been closed as Expired without line clearance.`);
            } else if (evalResult.hasCriticalFailure) {
                alert(`ALC Checklist Failed (Critical Gate Violation). Score: ${scorePercent}%. ${evalResult.criticalFailures.length} critical parameter(s) were non-compliant or partial. Forwarding to production for corrective actions.`);
            } else if (statusText === "Success - Pending Production") {
                alert(`ALC Checklist Submitted successfully with Success Score: ${scorePercent}%. Forwarding to production for corrective actions.`);
            } else if (statusText === "Completed") {
                alert(`ALC Cleared Successfully! Score: ${scorePercent}%`);
            } else {
                alert(`ALC Checklist Failed. Score: ${scorePercent}%. Forwarding to production for corrective actions.`);
            }

            // Route based on result and role
            if (isPreviousDay || statusText === "Completed") {
                dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY });
            } else if (isProductionUser || isProductUser) {
                dispatch({ type: 'SET_STATE', payload: ALC_STATES.PRODUCTION_ACTION });
            } else {
                dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY });
            }

        } catch (error: any) {
            console.error("Error submitting ALC checklist:", error);
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
                console.warn("Network error during submit, falling back to offline save:", error);
                await saveOffline();
            } else {
                alert("Dataverse Error: Failed to submit ALC checklist: " + error.message);
            }
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div id="section-checklist-filling">
            <ALCStepper />

            {/* Previous Day Warning Banner */}
            {state.isPreviousDay && (
                <div
                    id="previous-day-warning-banner"
                    style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fef3c7',
                        borderLeft: '4px solid #f59e0b',
                        padding: '12px 16px',
                        marginBottom: '15px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
                    <span>
                        <strong>Previous Day Observation:</strong> This request was initiated on a previous date. Any observations recorded will automatically close this tour session as <strong>Expired</strong> without granting line clearance.
                    </span>
                </div>
            )}

            {/* Executive Badge Strip */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginBottom: '15px', fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                <div>Shift Executive (Prod): <strong style={{ color: '#0f172a' }}>{state.sessionData?.productionExecutive || "Production Executive"}</strong></div>
                <div>| QA Executive: <strong style={{ color: '#0f172a' }}>{state.sessionData?.assignedQaName || "QA Executive"}</strong></div>
            </div>

            {/* Live Progress Tracker Banner matching source updateTracker() */}
            <div
                id="checklist-tracker-banner"
                style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '14px 20px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '14px', color: '#334155' }}>
                        <span id="tracker-summary-text">
                            <strong>{trackerStats.filledCount}</strong> of <strong>{TOTAL_ALC_QUESTIONS}</strong> checkpoints filled ({trackerStats.progressPercent}%)
                        </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ fontSize: '13px', color: '#475569' }}>
                            Est. Score: <strong id="tracker-estimated-score" style={{ fontSize: '15px', color: '#0f172a' }}>{trackerStats.estimatedScorePercent}%</strong>
                        </div>

                        <span
                            id="tracker-score-badge"
                            style={{
                                padding: '4px 10px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: '1px solid',
                                backgroundColor: trackerStats.hasLiveCriticalFailure ? '#fee2e2' : (trackerStats.isPass ? '#dcfce7' : '#fee2e2'),
                                color: trackerStats.hasLiveCriticalFailure ? '#b91c1c' : (trackerStats.isPass ? '#15803d' : '#b91c1c'),
                                borderColor: trackerStats.hasLiveCriticalFailure ? '#fca5a5' : (trackerStats.isPass ? '#bbf7d0' : '#fecaca')
                            }}
                            title={
                                trackerStats.hasLiveCriticalFailure
                                    ? `${trackerStats.liveCriticalFailureCount} critical parameter(s) non-compliant or partial`
                                    : (trackerStats.isPass ? "Passing score (>= 80% with no critical gate failures)" : "Score is below 80% threshold")
                            }
                        >
                            {trackerStats.hasLiveCriticalFailure ? "Fail (Critical Gate)" : (trackerStats.isPass ? "Pass" : "Fail (< 80%)")}
                        </span>
                    </div>
                </div>

                {/* Progress bar */}
                <div style={{ height: '7px', width: '100%', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '12px' }}>
                    <div
                        id="tracker-progress-bar"
                        style={{
                            height: '100%',
                            width: `${trackerStats.progressPercent}%`,
                            backgroundColor: '#2563eb',
                            transition: 'width 0.3s ease'
                        }}
                    ></div>
                </div>

                {/* Counter Badges */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px' }}>
                    <span style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '3px 8px', borderRadius: '4px', border: '1px solid #bbf7d0' }}>
                        Compliant (2): <strong id="tracker-count-compliant">{trackerStats.compliantCount}</strong>
                    </span>
                    <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '3px 8px', borderRadius: '4px', border: '1px solid #fde68a' }}>
                        Partial (1): <strong id="tracker-count-partial">{trackerStats.partialCount}</strong>
                    </span>
                    <span style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 8px', borderRadius: '4px', border: '1px solid #fecaca' }}>
                        Non-Compliant (0): <strong id="tracker-count-noncompliant">{trackerStats.nonCompliantCount}</strong>
                    </span>
                    <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                        Pending: <strong id="tracker-count-pending">{trackerStats.pendingCount}</strong>
                    </span>
                </div>
            </div>

            {/* Checklist Tables Grouped by Area */}
            {sections.map((section, sIdx) => (
                <div key={sIdx} className="bs-card" style={{ marginBottom: '20px', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
                    <div className="bs-card-header" style={{ backgroundColor: '#f1f5f9', padding: '12px 18px', borderBottom: '1px solid #cbd5e1' }}>
                        <h4 className="bs-card-title" style={{ margin: 0, fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>
                            {section.title}
                        </h4>
                    </div>

                    <div className="bs-card-body" style={{ padding: '0', overflowX: 'auto' }}>
                        <table className="bs-table" style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                                    <th style={{ width: '6%', padding: '10px' }}>Sr No.</th>
                                    <th style={{ width: '42%', padding: '10px', textAlign: 'left' }}>Description</th>
                                    <th style={{ width: '16%', padding: '10px' }}>Compliance Score</th>
                                    <th style={{ width: '18%', padding: '10px' }}>Remarks</th>
                                    <th style={{ width: '18%', padding: '10px' }}>Upload Image</th>
                                </tr>
                            </thead>
                            <tbody>
                                {section.items.map((item) => {
                                    const resp = responses[item.id] || { score: '', remarks: '' };
                                    const files = uploadedFiles[item.id] || [];
                                    const savedFiles = existingFiles[item.id] || [];
                                    const isSelectInvalid = invalidFields[`select_${item.id}`];
                                    const isRemarksInvalid = invalidFields[`remarks_${item.id}`];
                                    const isFileInvalid = invalidFields[`file_${item.id}`];

                                    return (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px' }}>{item.sequence}</td>
                                            <td style={{ padding: '10px', textAlign: 'left' }}>
                                                <span className="criteria-text">{item.title}</span>
                                                {item.isCritical && (
                                                    <span
                                                        className="alc-critical-badge"
                                                        style={{
                                                            marginLeft: '6px',
                                                            backgroundColor: '#fee2e2',
                                                            color: '#b91c1c',
                                                            border: '1px solid #fca5a5',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '3px'
                                                        }}
                                                        title="Critical Gate: Tour fails if this parameter is Non-Compliant or Partial"
                                                    >
                                                        <i className="fa fa-exclamation-triangle"></i> CRITICAL GATE
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                                <select
                                                    id={`select_${item.id}`}
                                                    className="form-select"
                                                    value={resp.score}
                                                    onChange={(e) => handleSelectChange(item.id, e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        borderColor: isSelectInvalid ? '#ef4444' : '#cbd5e1',
                                                        boxShadow: isSelectInvalid ? '0 0 0 0.2rem rgba(239, 68, 68, 0.25)' : 'none'
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    <option value="Compliant (2)">Compliant</option>
                                                    <option value="Partial (1)">Partial</option>
                                                    <option value="Non-Compliant (0)">Non-Compliant</option>
                                                </select>
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                                <input
                                                    type="text"
                                                    id={`remarks_${item.id}`}
                                                    className="form-control"
                                                    placeholder="Enter remarks if defect"
                                                    value={resp.remarks}
                                                    onChange={(e) => handleRemarksChange(item.id, e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        borderColor: isRemarksInvalid ? '#ef4444' : '#cbd5e1',
                                                        boxShadow: isRemarksInvalid ? '0 0 0 0.2rem rgba(239, 68, 68, 0.25)' : 'none'
                                                    }}
                                                />
                                            </td>
                                            <td style={{ padding: '10px' }}>
                                                <div
                                                    id={`file_${item.id}`}
                                                    className="custom-file-upload"
                                                    style={{
                                                        border: isFileInvalid ? '1px solid #ef4444' : '1px dashed #cbd5e1',
                                                        boxShadow: isFileInvalid ? '0 0 0 0.2rem rgba(239, 68, 68, 0.25)' : 'none',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#f8fafc'
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        onChange={(e) => handleFileChange(item.id, e.target.files)}
                                                        style={{ fontSize: '11px', width: '100%' }}
                                                    />

                                                    {/* Saved photos from Dataverse/SharePoint */}
                                                    {savedFiles.length > 0 && (
                                                        <div style={{ marginTop: '5px', fontSize: '11px', color: '#1d4ed8', textAlign: 'left' }}>
                                                            <div style={{ fontWeight: 600 }}>
                                                                <i className="fa fa-paperclip"></i> Saved Photos ({savedFiles.length}):
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
                                                                {savedFiles.map((sf, sfIdx) => (
                                                                    <span
                                                                        key={sfIdx}
                                                                        style={{
                                                                            backgroundColor: '#eff6ff',
                                                                            border: '1px solid #bfdbfe',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '3px',
                                                                            fontSize: '10px',
                                                                            color: '#1e40af'
                                                                        }}
                                                                        title={sf}
                                                                    >
                                                                        Photo {sfIdx + 1}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Selected files chips */}
                                                    {files.length > 0 && (
                                                        <div style={{ marginTop: '5px', textAlign: 'left' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#15803d' }}>
                                                                <i className="fa fa-check-circle"></i> {files.length} photo(s) selected:
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
                                                                {files.map((file, fIdx) => (
                                                                    <div
                                                                        key={fIdx}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            backgroundColor: '#f0fdf4',
                                                                            border: '1px solid #bbf7d0',
                                                                            padding: '2px 6px',
                                                                            borderRadius: '3px',
                                                                            fontSize: '11px',
                                                                            color: '#166534'
                                                                        }}
                                                                    >
                                                                        <span style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                                                            {file.name}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveFile(item.id, fIdx)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: '#991b1b',
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold',
                                                                                padding: '0 2px'
                                                                            }}
                                                                            title="Remove photo"
                                                                        >
                                                                            &times;
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {files.length === 0 && savedFiles.length === 0 && (
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                                            No image uploaded
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* Bottom Action Buttons matching source wrapper and pause capability */}
            <div
                className="tour-cyle-btn-wrapper"
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '25px 0'
                }}
            >
                <button
                    type="button"
                    id="pause-alc-btn"
                    onClick={handlePauseTour}
                    disabled={state.isLoading}
                    style={{
                        padding: '10px 22px',
                        backgroundColor: '#64748b',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <i className="fa fa-pause"></i> Pause Tour
                </button>

                <button
                    type="button"
                    id="submit-alc-btn"
                    onClick={handleSubmit}
                    disabled={state.isLoading}
                    style={{
                        padding: '10px 28px',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {state.isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span> Submitting...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-paper-plane"></i> Submit &amp; Evaluate
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ALCChecklist;
